import { Box } from "@mui/material"
import { AddOutlined } from "@mui-symbols-material/w300"
import { useNavigate, useSearch } from "@tanstack/react-router"
import type { SortingState } from "@tanstack/react-table"
import type { FC } from "react"
import { useEffect, useMemo, useRef } from "react"

import { Button } from "@/components/common/Button"
import type { RoleSortField } from "@/graphql/codegen/graphql"
import { SortingOrder } from "@/graphql/codegen/graphql"
import { useScrollLoadMore } from "@/hooks/useScrollLoadMore"
import { useThrottle } from "@/hooks/useThrottle"
import { queryClient } from "@/providers/GraphqlRouterProvider"
import { RolesTable } from "@/screens/SystemAdmin/components"
import { AddNewRoleDialog, DeleteRoleDialog, EditRoleDialog } from "@/screens/SystemAdmin/components/dialogs"
import {
  type RoleSortableAccessorKey,
  accessorKeyToRoleSortField,
  roleSortFieldToAccessoryKey,
} from "@/screens/SystemAdmin/constants"
import { useRoleColumns, useSystemAdminDialog } from "@/screens/SystemAdmin/hooks"
import { type RolesSearchFilters, useRolesSearchStore } from "@/screens/SystemAdmin/stores"
import { SystemAdminDialog } from "@/screens/SystemAdmin/types"

const RolesContainer: FC = () => {
  const navigate = useNavigate()
  const { field, order } = useSearch({ from: "/app/admin/system-admin/_layout/roles" })
  const { dialogOpen, setDialogOpen, selectedRole, setSelectedRole } = useSystemAdminDialog()

  const data = useRolesSearchStore(({ data }) => data)
  const fetching = useRolesSearchStore(({ fetching }) => fetching)
  const error = useRolesSearchStore(({ error }) => error)
  const executeSearch = useRolesSearchStore(({ executeSearch }) => executeSearch)
  const loadMore = useRolesSearchStore(({ loadMore }) => loadMore)
  const clearStore = useRolesSearchStore(({ clearStore }) => clearStore)

  const tableContainerRef = useRef<HTMLDivElement>(null)

  const columns = useRoleColumns({
    setSelectedRole,
    setDialogOpen,
  })

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearStore()
    }
  }, [clearStore])

  useEffect(() => {
    const searchFilters: RolesSearchFilters = {
      field,
      order,
    }
    executeSearch(queryClient, searchFilters)
  }, [field, order, executeSearch])

  const [throttledLoadMore] = useThrottle(() => {
    const searchFilters: RolesSearchFilters = {
      field,
      order,
    }
    loadMore(queryClient, searchFilters)
  }, 200)

  const scrollHandler = useScrollLoadMore({
    fetching,
    loadMore: throttledLoadMore,
    scrollContainerRef: tableContainerRef,
  })

  const sorting = useMemo(() => {
    if (!field) return []

    const tableSortField = roleSortFieldToAccessoryKey.get(field as RoleSortField)
    if (!tableSortField) return []

    return [
      {
        id: tableSortField,
        desc: order === SortingOrder.Desc,
      },
    ]
  }, [field, order])

  const handleSortingChange = (updaterFn: (state: SortingState) => SortingState) => {
    const newSorting = updaterFn(sorting)
    if (newSorting.length) {
      const sort = newSorting[0]
      const key = sort.id as RoleSortableAccessorKey
      const field = accessorKeyToRoleSortField.get(key) as RoleSortField
      const params = {
        field,
        order: sort.desc ? SortingOrder.Desc : SortingOrder.Asc,
      }

      navigate({
        search: params,
        to: "/app/admin/system-admin/roles",
        replace: true,
      })
    }
  }

  const handleDialogClose = () => {
    setDialogOpen(null)
  }

  const handleOnSuccess = () => {
    const searchFilters: RolesSearchFilters = {
      field,
      order,
    }
    executeSearch(queryClient, searchFilters, true)

    handleDialogClose()
  }

  return (
    <Box className='flex h-full flex-col gap-4'>
      <Box className={"flex justify-end"}>
        <Button
          startIcon={<AddOutlined />}
          appearance='outlined'
          onClick={() => setDialogOpen(SystemAdminDialog.ADD_NEW_ROLE)}
        >
          New Role
        </Button>
      </Box>

      <Box className='grow overflow-hidden'>
        <RolesTable
          data={data}
          scrollHandler={scrollHandler}
          tableContainerRef={tableContainerRef}
          handleSortingChange={handleSortingChange}
          columns={columns}
          sorting={sorting}
          fetching={fetching}
          error={error}
        />
      </Box>

      {dialogOpen === SystemAdminDialog.ADD_NEW_ROLE && (
        <AddNewRoleDialog isOpen={dialogOpen} onSuccess={handleOnSuccess} onClose={handleDialogClose} />
      )}
      {dialogOpen === SystemAdminDialog.EDIT_ROLE && selectedRole && (
        <EditRoleDialog
          role={selectedRole}
          isOpen={dialogOpen}
          onSuccess={handleOnSuccess}
          onClose={handleDialogClose}
        />
      )}
      {dialogOpen === SystemAdminDialog.DELETE_ROLE && selectedRole && (
        <DeleteRoleDialog
          role={selectedRole}
          isOpen={dialogOpen}
          onSuccess={handleOnSuccess}
          onClose={handleDialogClose}
        />
      )}
    </Box>
  )
}

export default RolesContainer
